import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

// Models
import { FirebaseSubjectListener } from 'src/app/models/firebase-listener/firebase-listener.model';
import { Conversation, ConversationCategory, ConversationList, Message } from 'src/app/services/messaging/models';
import { MessagingService } from 'src/app/services/messaging/messaging.service';
import { MuteService } from 'src/app/services/mute/mute.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { PushRegistrationService } from 'src/app/services/push-registration/push-registration.service';
import { TeamsService } from 'src/app/services/teams/teams.service';
import { UserService } from 'src/app/services/user/user.service';
import { BrowserNotificationService } from '../browser-notification/browser-notification.service';
import { FirebaseService } from '../firebase/firebase.service';
import { UnreadCountsStore } from './unread-counts.store';
import { isTeamAgentDm, notificationCopyFor, shouldNotifyFor, shouldPlaySound } from './unread-notify.util';

type LastMessagesReadMap = { [conversationId: Conversation['id']]: Message['id'] };

/**
 * The conversation kinds this listener follows, each with the roster window it
 * mirrors. Until 2026-09-23 only 'personal' (human DMs) was watched, so an
 * agent's answer — an 'ai' DM or a 'channel' team space — never produced a
 * browser notification or an unread count (Vincent: "It would be nice to have
 * browser notification when an agent answers and to see a badge in the
 * agent/team list with the number of new messages").
 */
const WATCHED: Array<{ category: ConversationCategory; max: number; closed?: boolean }> = [
  { category: 'personal', max: 15 },
  // Agent DMs — the Agents roster's own window (per-user cap 50).
  { category: 'ai', max: 200, closed: false },
  // Spaces and team spaces — the Spaces roster's window.
  { category: 'channel', max: 50, closed: false },
];

@Injectable({
  providedIn: 'root'
})
export class MessagingListenerService {

  public conversations: ConversationList = [];
  public listener: BehaviorSubject<ConversationList> | null = null;
  public totalUnreadCount$ = new BehaviorSubject<number>(0);

  private lastMessageReadIds: LastMessagesReadMap = {};
  /** Conversations whose participant read-marker has been fetched once (viewing keeps the cache current). */
  private readonly readMarkerFetched = new Set<string>();
  private activeConversationId: string | null = null;
  private readonly byCategory = new Map<ConversationCategory, ConversationList>();
  /** Categories whose FIRST snapshot has been applied — that one is history, never "new messages". */
  private readonly settled = new Set<ConversationCategory>();
  private listeners: FirebaseSubjectListener<Subject<ConversationList>>[] = [];
  /** The org the conversation queries were bound against. */
  private boundOrgId: string | null = null;
  private orgSub: Subscription | null = null;
  private notificationSound: HTMLAudioElement | null = null;
  /** Snapshots of the watched queries interleave; they are applied one at a time. */
  private queue: Promise<void> = Promise.resolve();

  constructor(
    private browserNotification: BrowserNotificationService,
    private firebase: FirebaseService,
    private messaging: MessagingService,
    private muteService: MuteService,
    private organization: OrganizationService,
    private platform: PlatformService,
    private router: Router,
    private user: UserService,
    private unreadStore: UnreadCountsStore,
    private teams: TeamsService,
    private pushRegistration: PushRegistrationService,
  ) {
    // This service is injected by AppComponent, so this constructor runs at app
    // boot — i.e. while the splash screen is up.
    //
    // Web: unchanged. Browsers show an unobtrusive, re-requestable permission
    // chip and users expect it there.
    //
    // Native (Capacitor): iOS grants exactly ONE notification prompt per install
    // and a denial is permanent, so spending it before the user has seen a single
    // message is the one thing you must not do. The request moves to real user
    // intent — opening a conversation (conversation-state.service.ts, which
    // already calls requestPermission() on every platform). Safety net for any
    // path that never opens a conversation: showNotification() re-requests while
    // its cached permission is still 'default'
    // (browser-notification.service.ts:33-36), so nothing ends up never asking.
    if (!this.platform.is('shell')) {
      this.browserNotification.requestPermission();
    }
    // The rail tells us which conversations it renders; the tab title counts those.
    this.unreadStore.visibleIds$.subscribe(() => this.updateTotalUnreadCount(this.conversations));

    // Backgrounded/killed mobile can't run this JS at all (see ../mobile/README.md),
    // so the FCM fan-out (push-notify.ts) runs server-side, independently of this
    // tab — it needs to read from Firestore, not ask us directly, whether to skip
    // the conversation the user is actually looking at. Clear on hide rather than
    // trust a stale value forever: a killed app never runs the ngOnDestroy path
    // that would otherwise clear it.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.writeActiveConversationPresence(null);
      } else if (this.activeConversationId) {
        this.writeActiveConversationPresence(this.activeConversationId);
      }
    });

    // Android's WebView does not reliably flip document.visibilityState to 'hidden'
    // on backgrounding (unlike a real browser tab) — confirmed on device: leaving the
    // app left activeConversationId stuck, silently suppressing every push. The
    // shell's own App plugin lifecycle (shell/src/main.ts) is authoritative here —
    // it mirrors real Android onPause/onResume — so mirror it the same way.
    window.addEventListener('crewdle:appStateChange', (event) => {
      const isActive = (event as CustomEvent<{ isActive: boolean }>).detail?.isActive;
      if (!isActive) {
        this.writeActiveConversationPresence(null);
      } else if (this.activeConversationId) {
        this.writeActiveConversationPresence(this.activeConversationId);
      }
    });
  }

  public listen() {
    if (this.listener) {
      return this.listener;
    }

    this.listener = new BehaviorSubject<ConversationList>([]);
    this.attach();
    // listenConversations() scopes conversations to the active org: an in-place
    // org switch (OrgSwitcherService) must re-query, or the rail badges and the
    // tab's unread count keep showing the org we left.
    this.orgSub = this.organization.orgId$.subscribe((orgId) => {
      if (orgId && this.boundOrgId && orgId !== this.boundOrgId) {
        this.rebind();
      }
    });

    return this.listener;
  }

  /** Tear the conversation queries down and bind them again against the active org, keeping subscribers. */
  private rebind(): void {
    this.detach();
    this.conversations = [];
    this.lastMessageReadIds = {};
    this.readMarkerFetched.clear();
    this.byCategory.clear();
    // The next snapshots are a fresh org, not "new messages" — no notifications
    // or sounds for them.
    this.settled.clear();
    this.listener?.next([]);
    this.publishCounts();
    this.attach();
  }

  private detach(): void {
    this.listeners.forEach((l) => l.unsubscribe());
    this.listeners = [];
  }

  private attach(): void {
    if (!this.listener) {
      return;
    }
    this.boundOrgId = this.organization.data?.id ?? null;

    for (const { category, max, closed } of WATCHED) {
      const listener = this.messaging.listenConversations(undefined, category, closed, max);
      this.listeners.push(listener);
      listener.subject.subscribe((conversations) => {
        if (!conversations) {
          return;
        }
        this.queue = this.queue
          .then(() => this.applySnapshot(category, conversations))
          .catch((error) => console.error('[messaging-listener] snapshot failed', error));
      });
    }
  }

  /**
   * Merge one query's snapshot into the combined list and settle every
   * conversation's unread count, notifying for the ones whose last message
   * just changed. Diffing is keyed by conversation id — the three queries do
   * not share an order.
   */
  private async applySnapshot(category: ConversationCategory, incoming: ConversationList): Promise<void> {
    const uid = this.user.user?.uid;
    if (!uid) {
      return;
    }
    // Channels are not org-scoped by the query (Spaces uses the org channel
    // index); keep every category org-clean here. Docs without an org are kept.
    this.byCategory.set(category, incoming.filter((c) =>
      !!c.id && (!c.organizationId || !this.boundOrgId || c.organizationId === this.boundOrgId)));

    const previous = new Map(this.conversations.map((c) => [c.id as string, c]));
    const next: ConversationList = [];
    for (const { category: cat } of WATCHED) {
      for (const conversation of this.byCategory.get(cat) ?? []) {
        next.push({ ...conversation });
      }
    }

    // Read markers once per conversation (the participant doc); viewing keeps
    // the cache current through updateCachedLastMessageRead.
    const unseen = next.filter((c) => !this.readMarkerFetched.has(c.id as string));
    if (unseen.length > 0) {
      unseen.forEach((c) => this.readMarkerFetched.add(c.id as string));
      Object.assign(this.lastMessageReadIds, await this.fetchLastMessageReadIds(unseen));
    }

    const mayNotify = this.settled.has(category);
    await Promise.all(next.map((entry) => this.settleCount(entry, previous.get(entry.id as string), uid, mayNotify)));
    this.settled.add(category);

    this.conversations = next;
    this.listener?.next(this.conversations);
    this.publishCounts();
  }

  /** Decide one conversation's unread count; notify when its last message is new to us. */
  private async settleCount(entry: Partial<Conversation>, old: Partial<Conversation> | undefined, uid: string, mayNotify: boolean): Promise<void> {
    const id = entry.id as string;
    const last = entry.lastMessage;

    // User is currently viewing this conversation — always 0, and caught up.
    if (this.activeConversationId === id) {
      if (last?.id) {
        this.lastMessageReadIds[id] = last.id;
      }
      entry.newMessageCount = '0';
      return;
    }

    const changed = !old || old.lastMessage?.id !== last?.id;
    if (!changed) {
      entry.newMessageCount = old?.newMessageCount || '0';
      return;
    }
    if (!last || last.sender === uid) {
      entry.newMessageCount = '0';
      return;
    }
    // A teamed agent talking to its team in its own conversation: nothing new
    // for the person — the count stands (no query, no notification).
    if (last.teamTraffic && old) {
      entry.newMessageCount = old.newMessageCount || '0';
      return;
    }
    if (mayNotify) {
      this.notify(entry, uid);
    }
    entry.newMessageCount = await this.getNewMessageCount(entry, this.lastMessageReadIds[id]);
  }

  private notify(conversation: Partial<Conversation>, uid: string): void {
    const id = conversation.id as string;
    // On the phone the server already sends this message as a push (and iOS draws it even with the app
    // open). A local notification as well would show it twice.
    if (this.pushRegistration.registered) {
      return;
    }
    // A project is followed in its space: its MANAGER's own conversation stays
    // quiet. Members are regular agents too (they may work on several
    // projects), so their own chats notify like any agent's.
    const managers = this.teams.hiddenRosterKeys.agentIds;
    if (isTeamAgentDm(conversation, (agentId) => managers.has(agentId))) {
      return;
    }
    const ctx = {
      uid,
      activeConversationId: this.activeConversationId,
      visible: document.visibilityState === 'visible',
      focused: document.hasFocus(),
      muted: this.muteService.isMuted(id),
    };
    if (!shouldNotifyFor(conversation.lastMessage, id, ctx)) {
      return;
    }
    const copy = notificationCopyFor(conversation);
    if (!copy) {
      return;
    }
    // Not awaited: a pending permission prompt must not hold up the counts.
    void this.browserNotification.showNotification(copy.title, {
      body: copy.body,
      icon: '/assets/icon/favicon.png',
      // One notification per conversation at a time — a newer answer replaces it.
      tag: `conversation:${id}`,
      // `data` is an opaque passthrough in the Notifications spec — browsers only
      // ever hand it back on the notification object, so this is inert on the
      // web. The native shell's Notification shim reads `data.deepLink` and
      // uses it to route a tap that arrives after the WebView was suspended or
      // killed, when the `onclick` below no longer exists. Field name per
      // native-packaging/04-push-notifications.md §4.1.
      data: { deepLink: `${copy.route}?conversationId=${id}` },
    }).then((notification) => {
      if (notification) {
        notification.onclick = () => {
          this.router.navigate([copy.route], { queryParams: copy.queryParams });
          window.focus();
        };
      }
    }).catch(() => { /* permission denied or unsupported — the badge still shows it */ });

    if (shouldPlaySound(ctx)) {
      this.playNotificationSound();
    }
  }

  public async getNewMessageCount(conversation: Partial<Conversation>, lastMessageId?: string): Promise<string> {
    if (!conversation.id) {
      return '0';
    }

    const messageCount = await this.getMessagesCountFromMessageId(conversation.id, lastMessageId);
    if (messageCount > 9) {
      return '9+';
    }

    return messageCount.toString();
  }

  public async fetchLastMessageReadIds(conversations: ConversationList): Promise<LastMessagesReadMap> {

    const promises = conversations
      .filter(conversation => !!conversation.id)
      .map(async conversation => ({
        conversationId: conversation.id as string,
        lastReadMessageId: await this.messaging.fetchLastReadMessageId(conversation.id as string, this.user.user.uid),
      }));

    const lastMessageReadIdsArray = await Promise.all(promises);
    const lastMessageReadIds: LastMessagesReadMap = lastMessageReadIdsArray.reduce(
      (acc, { conversationId, lastReadMessageId }) => ({ ...acc, [conversationId]: lastReadMessageId }), {}
    );

    return lastMessageReadIds;
  }

  public updateCachedLastMessageRead(conversationId: Conversation['id'] | undefined, messageId: Message['id'] | undefined) {
    if (!conversationId || !messageId) {
      return;
    }

    this.lastMessageReadIds[conversationId] = messageId;
  }

  /**
   * The conversation on screen: never counted, and its badge clears the moment
   * it opens rather than on the next snapshot.
   */
  public setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId;
    this.writeActiveConversationPresence(document.visibilityState === 'visible' ? conversationId : null);
    if (!conversationId) {
      return;
    }
    const conversation = this.conversations.find((c) => c.id === conversationId);
    if (!conversation) {
      return;
    }
    if (conversation.lastMessage?.id) {
      this.lastMessageReadIds[conversationId] = conversation.lastMessage.id;
    }
    if (conversation.newMessageCount && conversation.newMessageCount !== '0') {
      conversation.newMessageCount = '0';
      this.listener?.next(this.conversations);
      this.publishCounts();
    }
  }

  private publishCounts(): void {
    const counts = new Map<string, string>();
    for (const conversation of this.conversations) {
      if (conversation.id) {
        counts.set(conversation.id, conversation.newMessageCount || '0');
      }
    }
    this.unreadStore.setCounts(counts);
    this.updateTotalUnreadCount(this.conversations);
  }

  /** Tab-title total: the rail's rows once it has reported them, every watched conversation before that. */
  public updateTotalUnreadCount(allConversations: ConversationList): void {
    const mutedIds = this.muteService.mutedIds$.getValue();
    const visible = this.unreadStore.visibleIds$.getValue();
    let total = 0;
    for (const c of allConversations) {
      if (!c.id || mutedIds.has(c.id)) continue;
      if (visible && !visible.has(c.id)) continue;
      const count = c.newMessageCount;
      if (!count || count === '0') continue;
      if (count === '9+') {
        total += 10;
      } else {
        total += parseInt(count, 10) || 0;
      }
    }
    this.totalUnreadCount$.next(total);
  }

  /**
   * Read by push-notify.ts (crewdle-sdk-firebase) so a backgrounded/killed mobile
   * app — which cannot run this JS to decide for itself — still skips pushing a
   * notification for the conversation the user is already looking at, on any
   * device. Fire-and-forget: a dropped write costs one extra push at worst, not
   * a broken UI.
   */
  private writeActiveConversationPresence(conversationId: string | null): void {
    const uid = this.user.user?.uid;
    if (!uid) {
      return;
    }
    void this.firebase.globalFirestore.docSet(`users/${uid}`, { activeConversationId: conversationId }, { merge: true }).catch(() => {});
  }

  private playNotificationSound(): void {
    if (!this.notificationSound) {
      this.notificationSound = new Audio('/assets/sound/notification.mp3');
    }
    this.notificationSound.currentTime = 0;
    this.notificationSound.play().catch(() => {});
  }

  private async getMessagesCountFromMessageId(conversationId: string, lastMessageId?: string): Promise<number> {
    if (!lastMessageId) {
      // No read marker means the user has never opened this conversation.
      // Count the latest root messages as unread so unopened conversations still
      // contribute to the in-app unread badge without fetching the full history.
      return await this.messaging.countUnreadInLatest(conversationId);
    }

    return await this.messaging.getMessagesCountAfter(conversationId, lastMessageId);
  }

}
